<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header class="flex items-end justify-between">
      <div>
        <h2 class="text-4xl font-black tracking-tight">
          Campaigns
        </h2>
        <p class="text-slate-400 mt-2 max-w-2xl">
          Plan, evaluate, and manage influencer campaigns before activation.
        </p>
      </div>

    </header>

    <!-- KPIs -->
    <section class="grid grid-cols-1 md:grid-cols-4 gap-8">
      <div
        v-for="kpi in kpis"
        :key="kpi.label"
        class="bg-white p-8 rounded-3xl premium-shadow
               border border-slate-50"
      >
        <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
          {{ kpi.label }}
        </p>
        <h3 class="text-4xl font-black mt-2">
          {{ kpi.value }}
        </h3>
      </div>
    </section>

    <!-- CAMPAIGN LIST -->
    <section
      class="bg-white rounded-[2.5rem] premium-shadow
             border border-slate-50 overflow-hidden"
    >
      <!-- TABLE HEADER -->
      <div class="grid grid-cols-12 px-8 py-5 border-b border-slate-50 text-[11px]
                  font-bold uppercase tracking-wider text-slate-400">
        <div class="col-span-3">Campaign</div>
        <div class="col-span-2">Objective</div>
        <div class="col-span-2">Influencers</div>
        <div class="col-span-2">Status</div>
        <div class="col-span-2">Last Updated</div>
        <div class="col-span-1"></div>
      </div>

      <!-- ROWS -->
      <div
        v-for="c in campaigns"
        :key="c.id"
        class="grid grid-cols-12 px-8 py-6 border-b border-slate-50
               hover:bg-slate-50/50 transition"
      >
        <!-- Campaign -->
        <div class="col-span-3">
          <p class="font-bold text-slate-900">
            {{ c.name }}
          </p>
          <p class="text-xs text-slate-400">
            {{ c.industry }}
          </p>
        </div>

        <!-- Objective -->
        <div class="col-span-2 text-sm">
          {{ c.objective }}
        </div>

        <!-- Influencers -->
        <div class="col-span-2 text-sm">
          {{ c.influencers }} selected
        </div>

        <!-- Status -->
        <div class="col-span-2">
          <span
            class="px-3 py-1 rounded-full text-xs font-bold"
            :class="statusClass(c.status)"
          >
            {{ c.status }}
          </span>
        </div>

        <!-- Updated -->
        <div class="col-span-2 text-sm text-slate-400">
          {{ c.updated }}
        </div>

        <!-- Action -->
        <div class="col-span-1 text-right">
          <button
            @click="viewDt(c)"
            class="text-xs font-bold uppercase tracking-wider
                   border-b border-slate-900"
          >
            View
          </button>
        </div>
      </div>
    </section>

  </section>
</template>
<script>
import { useToast } from "vue-toastification";

import {
  getCampaignKpisApi,
  getCampaignListApi
} from "../apis/influencerapis";

export default {
  name: "CampaignsAc",

  data() {
    return {

      kpis: [],
      campaigns: [],

      toast: null,
      user: null,
      loading: false
    };
  },

  async mounted() {

    this.toast = useToast();

    const ldataRaw = sessionStorage.getItem("ldata");
    this.user = ldataRaw ? JSON.parse(ldataRaw) : null;

    if (!this.user || !this.user.user_id) {
      window.location.href = "/login";
      return;
    }

    await this.loadKpis();
    await this.loadCampaigns();
  },

  methods: {

    // ================= LOAD KPIs =================
    async loadKpis() {

      const res = await getCampaignKpisApi(this.user.user_id);

      if (!res.status) {
        this.toast.error(res.success || "Failed to load KPIs");
        return;
      }

      this.kpis = res.kpis || [];
    },

    // ================= LOAD CAMPAIGNS =================
    async loadCampaigns() {

      if (this.loading) return;

      this.loading = true;

      const res = await getCampaignListApi(this.user.user_id);

      this.loading = false;

      if (!res.status) {
        this.toast.error(res.success || "Failed to load campaigns");
        return;
      }

      // ✅ Map backend → UI format
      this.campaigns = (res.campaigns || []).map(c => ({

        id: c.campaign_id,
        name: c.campaign_title,
        industry: c.campaign_type,
        objective: c.campaign_desc,
        influencers: c.influencers || 0,
        status: c.campaign_status,
        updated: c.campaign_datetime || "recent"

      }));
    },

    // ================= NAVIGATION =================
    viewDt(campaign) {

      window.location.href = "/campaigns-details/" + campaign.id;
        // window.location.href = "/agency/campaigns/details/"+campaign.id;
    },

    // ================= STATUS STYLES =================
    statusClass(status) {

      if (status === "Draft")
        return "bg-slate-100 text-slate-700";

      if (status === "Ready")
        return "bg-amber-50 text-amber-700";

      if (status === "Accepted")
        return "bg-emerald-50 text-emerald-700";

      if (status === "Rejected")
        return "bg-red-50 text-red-700";

      if (status === "Archived")
        return "bg-slate-50 text-slate-400";

      return "";
    }
  }
};
</script>
<style scoped>
/* ===== Mobile only fixes ===== */
@media (max-width: 640px) {

  /* Outer container spacing */
  section.max-w-\[1440px\] {
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }

  /* Header */
  header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  h2 {
    font-size: 1.75rem;
    line-height: 1.2;
  }

  /* KPI cards */
  .grid.md\:grid-cols-4 {
    gap: 1rem;
  }

  .grid.md\:grid-cols-4 > div {
    padding: 1.25rem;
    border-radius: 1.25rem;
  }

  /* Hide table header on mobile */
  .grid.grid-cols-12.px-8.py-5 {
    display: none;
  }

  /* Campaign rows → card layout */
  .grid.grid-cols-12.px-8.py-6 {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 1rem;
    margin: 0.75rem;
    background: #fff;
  }

  /* Reset column spans */
  .grid.grid-cols-12 > div {
    grid-column: span 12 / span 12;
  }

  /* Campaign name */
  .grid.grid-cols-12 .col-span-3 p:first-child {
    font-size: 0.95rem;
  }

  /* Meta text */
  .grid.grid-cols-12 .text-sm,
  .grid.grid-cols-12 .text-xs {
    font-size: 0.8rem;
  }

  /* Status badge */
  .grid.grid-cols-12 .col-span-2 span {
    display: inline-block;
    margin-top: 0.25rem;
  }

  /* Action button */
  .grid.grid-cols-12 .col-span-1 {
    text-align: left;
    margin-top: 0.5rem;
  }
}
</style>
